import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { LaunchPricingNotice } from '../components/LaunchPricingNotice';
import {
  Check,
  X,
  Euro,
  Percent,
  Shield,
  CreditCard,
  Calculator,
  HelpCircle,
  ArrowRight,
  Sparkles,
  CheckCircle,
  Ban,
  Gift,
  TrendingUp,
  Clock,
  Lock,
  Zap
} from 'lucide-react';

export default function CommissioniPage() {
  // Esempi di calcolo commissioni
  const examples = [
    { price: 50, commission: 4, youGet: 46 },
    { price: 100, commission: 8, youGet: 92 },
    { price: 250, commission: 20, youGet: 230 },
    { price: 500, commission: 40, youGet: 460 },
    { price: 1000, commission: 80, youGet: 920 },
  ];

  const includedInCommission = [
    { text: 'Elaborazione pagamenti Stripe', icon: CreditCard },
    { text: 'Protezione antifrode', icon: Shield },
    { text: 'Hosting e infrastruttura', icon: Zap },
    { text: 'Assistenza clienti', icon: HelpCircle },
    { text: 'Messaggistica integrata', icon: Check },
    { text: 'Gestione ordini', icon: Check },
    { text: 'Analytics e statistiche', icon: TrendingUp },
    { text: 'Aggiornamenti piattaforma', icon: Check },
  ];

  const noCosts = [
    'Nessun canone mensile',
    'Nessuna commissione di iscrizione',
    'Nessun costo per inserimento prodotti',
    'Nessun costo per le foto',
    'Nessuna commissione sulle richieste ricevute',
    'Nessun costo nascosto',
  ];

  const faqs = [
    {
      q: 'Quando viene applicata la commissione?',
      a: 'La commissione dell\'8% viene trattenuta automaticamente al momento del pagamento da parte del cliente (con un minimo di €1 sugli ordini piccoli). Tu ricevi direttamente il netto sul tuo conto Stripe.'
    },
    {
      q: 'Chi paga le spese di spedizione?',
      a: 'Le spese di spedizione sono a carico del cliente e vengono aggiunte al prezzo del prodotto. Non applichiamo commissioni sulle spese di spedizione.'
    },
    {
      q: 'Quando ricevo il pagamento?',
      a: 'Il pagamento viene trasferito sul tuo conto Stripe immediatamente dopo la conferma dell\'ordine. I tempi di accredito sul tuo conto bancario dipendono da Stripe (solitamente 2-7 giorni lavorativi).'
    },
    {
      q: 'Ci sono costi per il piano Premium?',
      a: 'Il piano Premium costa €10/mese e offre vantaggi come risposte illimitate, priorità sulle richieste e badge esclusivo. La commissione dell\'8% rimane invariata.'
    },
    {
      q: 'Come funziona per ordini con più artigiani?',
      a: 'Se un cliente ordina prodotti da più artigiani, ogni artigiano riceve il proprio pagamento separatamente, con la commissione dell\'8% applicata individualmente.'
    },
    {
      q: 'Posso emettere fattura al cliente?',
      a: 'Sì, se sei un artigiano con P.IVA puoi emettere fattura direttamente al cliente. La commissione di Rarifex è già inclusa nel prezzo e non richiede fatturazione separata.'
    }
  ];

  return (
    <>
      <Helmet>
        <title>Commissioni Rarifex | 8% Tutto Incluso | Trasparenza Totale</title>
        <meta name="description" content="Commissioni semplici e trasparenti su Rarifex: solo l'8% sul venduto, tutto incluso. Nessun costo nascosto, nessun canone mensile. Scopri quanto guadagni." />
        <meta name="keywords" content="commissioni Rarifex, costi vendita artigianato, commissioni marketplace, vendere online commissioni, 8% commissione" />
        <link rel="canonical" href="https://rarifex.com/commissioni" />
        
        {/* Open Graph */}
        <meta property="og:title" content="Commissioni Rarifex | 8% Tutto Incluso" />
        <meta property="og:description" content="Una sola commissione, tutto incluso: 8% sul venduto. Nessun costo nascosto, nessun canone mensile." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://rarifex.com/commissioni" />
        
        {/* Schema.org */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            "name": "Commissioni Rarifex",
            "description": "Commissioni trasparenti per vendere su Rarifex: 8% sul venduto, tutto incluso",
            "publisher": {
              "@type": "Organization",
              "name": "Rarifex",
              "url": "https://rarifex.com"
            }
          })}
        </script>
      </Helmet>

      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-[#008080] via-[#006666] to-[#004d4d] text-white py-16 md:py-24">
          <div className="max-w-6xl mx-auto px-4">
            <div className="text-center max-w-3xl mx-auto">
              <Badge className="mb-4 bg-white/20 text-white border-white/30 hover:bg-white/30">
                <Euro className="w-3 h-3 mr-1" />
                Trasparenza Totale
              </Badge>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
                Una Sola Commissione
              </h1>
              <p className="text-xl md:text-2xl text-white/90 mb-4">
                <span className="text-5xl md:text-7xl font-bold">8%</span>
              </p>
              <p className="text-xl text-white/80 mb-8">
                Tutto incluso. Nessun costo nascosto. Mai.
              </p>
              <div className="flex flex-wrap gap-4 justify-center">
                <Button 
                  size="lg" 
                  className="bg-white text-[#008080] hover:bg-slate-100"
                  asChild
                >
                  <Link to="/plans">
                    Inizia a Vendere
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Main Commission Card */}
        <section className="py-16 md:py-20 -mt-8">
          <div className="max-w-4xl mx-auto px-4">
            <LaunchPricingNotice className="mb-8" />
            <Card className="p-8 md:p-12 shadow-2xl border-0 bg-white relative overflow-hidden">
              {/* Decorative element */}
              <div className="absolute top-0 right-0 w-32 h-32 bg-[#008080]/5 rounded-bl-full" />
              
              <div className="text-center mb-10">
                <div className="inline-flex items-center justify-center w-20 h-20 bg-[#008080]/10 rounded-2xl mb-4">
                  <Percent className="w-10 h-10 text-[#008080]" />
                </div>
                <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-2">
                  8% su Ogni Vendita
                </h2>
                <p className="text-lg text-slate-600">
                  Semplice, chiaro, onesto — <span className="text-[#008080] font-semibold">minimo €1</span> per ordini piccoli
                </p>
              </div>

              {/* What's included */}
              <div className="bg-[#008080]/5 rounded-2xl p-6 md:p-8 mb-8">
                <h3 className="font-bold text-lg text-slate-900 mb-4 flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-[#008080]" />
                  Cosa include la commissione
                </h3>
                <div className="grid md:grid-cols-2 gap-3">
                  {includedInCommission.map((item, index) => (
                    <div key={index} className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-white rounded-lg flex items-center justify-center shadow-sm">
                        <item.icon className="w-4 h-4 text-[#008080]" />
                      </div>
                      <span className="text-slate-700">{item.text}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Zero costs */}
              <div className="border-2 border-dashed border-emerald-200 bg-emerald-50/50 rounded-2xl p-6 md:p-8">
                <h3 className="font-bold text-lg text-slate-900 mb-4 flex items-center gap-2">
                  <Gift className="w-5 h-5 text-emerald-600" />
                  Zero Costi Nascosti
                </h3>
                <div className="grid md:grid-cols-2 gap-2">
                  {noCosts.map((item, index) => (
                    <div key={index} className="flex items-center gap-2 text-slate-700">
                      <Ban className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                      <span>{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </Card>
          </div>
        </section>

        {/* Calculator Section */}
        <section className="py-16 bg-slate-50">
          <div className="max-w-4xl mx-auto px-4">
            <div className="text-center mb-10">
              <Calculator className="w-12 h-12 text-[#008080] mx-auto mb-4" />
              <h2 className="text-3xl font-bold text-slate-900 mb-4">
                Calcola Quanto Guadagni
              </h2>
              <p className="text-lg text-slate-600">
                Ecco alcuni esempi pratici di quanto ricevi per ogni vendita
              </p>
            </div>

            <Card className="overflow-hidden border-0 shadow-lg">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-[#008080] text-white">
                    <tr>
                      <th className="px-6 py-4 text-left font-semibold">Prezzo Prodotto</th>
                      <th className="px-6 py-4 text-center font-semibold">Commissione (8%)</th>
                      <th className="px-6 py-4 text-right font-semibold">Tu Ricevi</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {examples.map((example, index) => (
                      <tr key={index} className="hover:bg-slate-50 transition-colors">
                        <td className="px-6 py-4 font-medium text-slate-900">
                          €{example.price.toFixed(2)}
                        </td>
                        <td className="px-6 py-4 text-center text-slate-500">
                          -€{example.commission.toFixed(2)}
                        </td>
                        <td className="px-6 py-4 text-right font-bold text-[#008080]">
                          €{example.youGet.toFixed(2)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="bg-slate-50 px-6 py-4 text-center">
                <p className="text-sm text-slate-600">
                  💡 Sugli ordini più piccoli, la commissione Rarifex parte da un
                  minimo di <strong>€1</strong>: così possiamo continuare a offrirti
                  la piattaforma senza costi fissi mensili.
                </p>
              </div>
            </Card>
          </div>
        </section>

        {/* Comparison Section */}
        <section className="py-16">
          <div className="max-w-5xl mx-auto px-4">
            <div className="text-center mb-10">
              <h2 className="text-3xl font-bold text-slate-900 mb-4">
                Perché Siamo Diversi
              </h2>
              <p className="text-lg text-slate-600">
                Confronta le nostre commissioni con altri marketplace
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              {/* Rarifex */}
              <Card className="p-6 border-2 border-[#008080] bg-[#008080]/5 relative">
                <div className="absolute -top-3 left-4">
                  <Badge className="bg-[#008080] text-white">Rarifex</Badge>
                </div>
                <div className="mt-4">
                  <div className="text-4xl font-bold text-[#008080] mb-2">8%</div>
                  <p className="text-slate-600 mb-4">Tutto incluso, sempre</p>
                  <ul className="space-y-2">
                    <li className="flex items-center gap-2 text-slate-700">
                      <Check className="w-5 h-5 text-emerald-500" />
                      Commissione unica e chiara
                    </li>
                    <li className="flex items-center gap-2 text-slate-700">
                      <Check className="w-5 h-5 text-emerald-500" />
                      Nessun costo mensile (piano Free)
                    </li>
                    <li className="flex items-center gap-2 text-slate-700">
                      <Check className="w-5 h-5 text-emerald-500" />
                      Pagamenti Stripe inclusi
                    </li>
                    <li className="flex items-center gap-2 text-slate-700">
                      <Check className="w-5 h-5 text-emerald-500" />
                      Assistenza inclusa
                    </li>
                    <li className="flex items-center gap-2 text-slate-700">
                      <Check className="w-5 h-5 text-emerald-500" />
                      Prodotti illimitati
                    </li>
                  </ul>
                </div>
              </Card>

              {/* Altri Marketplace */}
              <Card className="p-6 border border-slate-200 bg-slate-50/50 relative">
                <div className="absolute -top-3 left-4">
                  <Badge variant="outline" className="bg-white">Altri Marketplace</Badge>
                </div>
                <div className="mt-4">
                  <div className="text-4xl font-bold text-slate-400 mb-2">10-20%+</div>
                  <p className="text-slate-500 mb-4">Costi variabili e nascosti</p>
                  <ul className="space-y-2">
                    <li className="flex items-center gap-2 text-slate-500">
                      <X className="w-5 h-5 text-red-400" />
                      Commissione base + commissione transazione
                    </li>
                    <li className="flex items-center gap-2 text-slate-500">
                      <X className="w-5 h-5 text-red-400" />
                      Canoni mensili obbligatori
                    </li>
                    <li className="flex items-center gap-2 text-slate-500">
                      <X className="w-5 h-5 text-red-400" />
                      Costi per inserzione
                    </li>
                    <li className="flex items-center gap-2 text-slate-500">
                      <X className="w-5 h-5 text-red-400" />
                      Costi per promozione obbligatori
                    </li>
                    <li className="flex items-center gap-2 text-slate-500">
                      <X className="w-5 h-5 text-red-400" />
                      Limiti sul numero di prodotti
                    </li>
                  </ul>
                </div>
              </Card>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-16 bg-slate-50">
          <div className="max-w-4xl mx-auto px-4">
            <div className="text-center mb-10">
              <HelpCircle className="w-12 h-12 text-[#008080] mx-auto mb-4" />
              <h2 className="text-3xl font-bold text-slate-900 mb-4">
                Domande Frequenti
              </h2>
            </div>

            <div className="space-y-4">
              {faqs.map((faq, index) => (
                <Card key={index} className="p-6">
                  <h3 className="font-bold text-slate-900 mb-2 flex items-start gap-2">
                    <span className="w-6 h-6 bg-[#008080]/10 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-[#008080] text-sm font-bold">{index + 1}</span>
                    </span>
                    {faq.q}
                  </h3>
                  <p className="text-slate-600 ml-8">{faq.a}</p>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Trust Section */}
        <section className="py-16">
          <div className="max-w-4xl mx-auto px-4">
            <div className="grid md:grid-cols-3 gap-6 text-center">
              <div>
                <div className="w-16 h-16 bg-[#008080]/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Shield className="w-8 h-8 text-[#008080]" />
                </div>
                <h3 className="font-bold text-slate-900 mb-2">Pagamenti Sicuri</h3>
                <p className="text-sm text-slate-600">
                  Tutti i pagamenti sono gestiti da Stripe, leader mondiale nei pagamenti online
                </p>
              </div>
              <div>
                <div className="w-16 h-16 bg-[#008080]/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Lock className="w-8 h-8 text-[#008080]" />
                </div>
                <h3 className="font-bold text-slate-900 mb-2">Nessun Vincolo</h3>
                <p className="text-sm text-slate-600">
                  Puoi chiudere il tuo account in qualsiasi momento, senza penali
                </p>
              </div>
              <div>
                <div className="w-16 h-16 bg-[#008080]/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Clock className="w-8 h-8 text-[#008080]" />
                </div>
                <h3 className="font-bold text-slate-900 mb-2">Pagamenti Rapidi</h3>
                <p className="text-sm text-slate-600">
                  Ricevi i tuoi guadagni direttamente sul tuo conto Stripe
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 md:py-24 bg-gradient-to-br from-[#008080] to-[#004d4d] text-white">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <Sparkles className="w-12 h-12 mx-auto mb-4 opacity-80" />
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Inizia a Vendere Oggi
            </h2>
            <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
              Registrati gratuitamente e inizia a vendere le tue creazioni. 
              Paghi solo quando vendi.
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <Button 
                size="lg" 
                className="bg-white text-[#008080] hover:bg-slate-100"
                asChild
              >
                <Link to="/plans">
                  Crea il Tuo Negozio Gratis
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Link>
              </Button>
              <Button 
                size="lg" 
                variant="outline" 
                className="border-white text-white hover:bg-white/10"
                asChild
              >
                <Link to="/come-vendere">
                  Come Funziona
                </Link>
              </Button>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}
